import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
} from '@nextui-org/react';
import { debounce } from 'lodash';
import { useSearchParams } from 'next/navigation';
import type { Dispatch, SetStateAction } from 'react';
import React, { useState } from 'react';

import { HeaderSearchIcon, IconArrowDown, IconFilter, IconLeft, SortMobileIcon } from '@/assets';
import { Buttons } from '@/components/button';
import LayoutList from '@/components/list-layout';
import HotTags from '@/contents/marketplace/HotTags';
import useCreateQueryString from '@/hooks/useCreateQueryString';
import useGetWidth from '@/hooks/useGetWidth';

import type { TypeLayout } from '../list-layout/useListLayout';

export enum SORT {
  desc = 'DESC',
  asc = 'ASC',
}

export enum SORT_COLLECTION {
  date_desc = 'createdAt_DESC',
  date_asc = 'createdAt_ASC',
  volume_desc = 'tradingVolume_DESC',
  volume_asc = 'tradingVolume_ASC',
}

const LIST_SORT = [
  {
    key: SORT.desc,
    title: 'Date created (newest)',
  },
  {
    key: SORT.asc,
    title: 'Date created (oldest)',
  },
];

const LIST_SORT_COLLECTION = [
  {
    key: SORT_COLLECTION.date_desc,
    title: 'Date created (newest)',
  },
  {
    key: SORT_COLLECTION.date_asc,
    title: 'Date created (oldest)',
  },
  {
    key: SORT_COLLECTION.volume_desc,
    title: 'Volume high to low',
  },
  {
    key: SORT_COLLECTION.volume_asc,
    title: 'Volume low to hight',
  },
];

const ListBarMarket: React.FC<{
  type?: TypeLayout;
  changeTypeLayout?: (type: TypeLayout) => void;
  openFilter: boolean;
  setOpenFilter: Dispatch<SetStateAction<boolean>>;
  handleClearFilter: () => void;
  hasHotTag?: boolean;
  searchPlaceholder?: string;
  hasClearAll?: boolean;
  hasFilter?: boolean;
  sortArtwork?: boolean;
  sortCollection?: boolean;
}> = ({
  type,
  changeTypeLayout,
  openFilter,
  setOpenFilter,
  handleClearFilter,
  hasHotTag = true,
  searchPlaceholder = 'Search',
  hasClearAll = false,
  hasFilter = true,
  sortArtwork = true,
  sortCollection = false,
}) => {
  const searchParams = useSearchParams();
  const createQueryString = useCreateQueryString();
  const screenWidth = useGetWidth();
  const [searchValue, setSearchValue] = useState(searchParams.get('name') ?? '');

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        {hasFilter && (
          <Button
            startContent={openFilter ? <IconLeft /> : <IconFilter />}
            className="m-0 mr-3 flex min-w-[100px] cursor-pointer items-center rounded-[100px] bg-gray-50 px-4 py-[10px] text-14 font-normal"
            onClick={() => setOpenFilter(!openFilter)}
          >
            Filter
          </Button>
        )}
        {hasClearAll && (
          <Buttons
            typeBtn="cancel"
            className="mr-3 text-15 font-semibold md:hidden"
            onClick={() => {
              handleClearFilter();
              setSearchValue('');
            }}
          >
            Clear all
          </Buttons>
        )}

        <div className="mr-3 w-full rounded-[100px] bg-gray-50">
          <Input
            defaultValue={searchValue}
            classNames={{
              base: 'flex justify-center',
              input: ['dark:placeholder:text-white font-normal text-14 pl-1', 'rounded-[100px]'],
              innerWrapper: ['bg-transparent', 'rounded-[100px]'],
              inputWrapper: ['bg-transparent', 'rounded-[100px]'],
            }}
            onChange={debounce((e) => createQueryString('name', e.target.value), 500)}
            startContent={<HeaderSearchIcon />}
            placeholder={searchPlaceholder}
          />
        </div>

        {sortArtwork && (
          <div>
            <Dropdown>
              <DropdownTrigger>
                {screenWidth > 767 ? (
                  <div className="flex w-[210px] cursor-pointer items-center justify-between rounded-[100px] bg-[#f8f9fd] px-[16px] py-[12px] text-[14px] text-black md:hidden sm:w-full">
                    {searchParams.get('createdAt')
                      ? LIST_SORT.find((item) => item.key === searchParams.get('createdAt'))?.title
                      : LIST_SORT[0]?.title}{' '}
                    <IconArrowDown className="-mt-1 ml-1" />
                  </div>
                ) : (
                  <Button className="m-0 hidden h-fit min-w-0 rounded-full bg-gray-50 p-2 md:block">
                    <SortMobileIcon />
                  </Button>
                )}
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Static Actions"
                onAction={(e: any) => createQueryString('createdAt', e)}
              >
                {LIST_SORT.map((item) => (
                  <DropdownItem key={item.key}>{item.title}</DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
        {sortCollection && (
          <div>
            <Dropdown>
              <DropdownTrigger>
                {screenWidth > 767 ? (
                  <div className="flex w-[210px] cursor-pointer items-center justify-between rounded-[100px] bg-[#f8f9fd] px-[16px] py-[12px] text-[14px] text-black md:hidden sm:w-full">
                    {searchParams.get('createdAt')
                      ? searchParams.get('createdAt') === 'DESC'
                        ? 'Date created (newest)'
                        : 'Date created (oldest)'
                      : searchParams.get('tradingVolume') === 'DESC'
                      ? 'Volume high to low'
                      : 'Volume low to hight'}
                    <IconArrowDown className="-mt-1 ml-1" />
                  </div>
                ) : (
                  <Button className="m-0 hidden h-fit min-w-0 rounded-full bg-gray-50 p-2 md:block">
                    <SortMobileIcon />
                  </Button>
                )}
              </DropdownTrigger>
              <DropdownMenu
                aria-label="Static Actions"
                onAction={(e: any) => {
                  const [name, value] = e.split('_');
                  createQueryString(
                    name,
                    value,
                    undefined,
                    name === 'tradingVolume' ? 'createdAt' : 'tradingVolume',
                  );
                }}
              >
                {LIST_SORT_COLLECTION.map((item) => (
                  <DropdownItem key={item.key}>{item.title}</DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        )}
        {type && changeTypeLayout && (
          <div className="ml-3">
            <LayoutList type={type} changeTypeLayout={changeTypeLayout} />
          </div>
        )}
      </div>
      {hasHotTag && <HotTags />}
    </div>
  );
};

export default ListBarMarket;
